import { useEffect } from "react";
import { Keyboard } from "@capacitor/keyboard";
import { isIOSPlatform } from "../utils/platformFeatures";

const KEYBOARD_THRESHOLD = 80;

export const getHealthVisibleMetrics = ({
  baselineHeight,
  viewportHeight,
  viewportTop = 0,
  nativeKeyboardHeight = 0,
}) => {
  const safeBaseline = Math.max(0, Number(baselineHeight) || 0);
  const safeTop = Math.max(0, Number(viewportTop) || 0);
  const visualBottom = safeTop + Math.max(0, Number(viewportHeight) || safeBaseline);
  const nativeBottom = nativeKeyboardHeight > 0
    ? Math.max(safeTop, safeBaseline - nativeKeyboardHeight)
    : visualBottom;
  const bottom = Math.min(visualBottom, nativeBottom);
  const height = Math.max(1, bottom - safeTop);

  return {
    top: safeTop,
    height,
    inset: Math.max(0, safeBaseline - (safeTop + height)),
  };
};

export const getHealthFieldScrollDelta = (rect, metrics) => {
  const visibleTop = metrics.top + 72;
  const visibleBottom = metrics.top + metrics.height - 24;

  if (rect.bottom > visibleBottom) return rect.bottom - visibleBottom;
  if (rect.top < visibleTop) return rect.top - visibleTop;
  return 0;
};

const findScrollContainer = (field) => {
  let element = field.parentElement;

  while (element && element !== document.body && element !== document.documentElement) {
    const style = window.getComputedStyle(element);
    if (/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight + 2) {
      return element;
    }
    element = element.parentElement;
  }

  return document.scrollingElement || document.documentElement;
};

export default function useHealthKeyboardViewport(detailPageRef) {
  useEffect(() => {
    const viewport = window.visualViewport;
    const root = document.documentElement;
    let baselineHeight = Math.max(window.innerHeight || 0, viewport?.height || 0);
    let nativeKeyboardHeight = 0;
    let frameId = 0;
    let disposed = false;
    let settleTimers = [];
    let nativeHandles = [];

    const activeField = () => {
      const field = document.activeElement;
      if (!(field instanceof HTMLElement)) return null;
      if (!field.matches("input, textarea, select, [contenteditable='true']")) return null;
      return detailPageRef.current?.contains(field) ? field : null;
    };

    const visibleMetrics = () => getHealthVisibleMetrics({
      baselineHeight,
      viewportHeight: viewport?.height || window.innerHeight || baselineHeight,
      viewportTop: viewport?.offsetTop || 0,
      nativeKeyboardHeight,
    });

    const revealActiveField = (behavior = "smooth") => {
      const field = activeField();
      if (!field) return;

      const delta = getHealthFieldScrollDelta(field.getBoundingClientRect(), visibleMetrics());
      if (Math.abs(delta) < 1) return;

      const scroller = findScrollContainer(field);
      if (scroller === document.documentElement || scroller === document.body || scroller === document.scrollingElement) {
        window.scrollBy({ top: delta, left: 0, behavior });
      } else {
        scroller.scrollTo({ top: scroller.scrollTop + delta, behavior });
      }
    };

    const updateViewport = (behavior = "smooth") => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const field = activeField();
        if (!field && nativeKeyboardHeight <= 0) {
          baselineHeight = Math.max(baselineHeight, window.innerHeight || 0, viewport?.height || 0);
        }

        const metrics = visibleMetrics();
        const viewportKeyboardOpen = metrics.inset > KEYBOARD_THRESHOLD
          && Math.abs((viewport?.scale || 1) - 1) < 0.05;
        const keyboardOpen = !!field && (nativeKeyboardHeight > 0 || viewportKeyboardOpen);

        root.style.setProperty("--health-keyboard-inset", `${Math.round(keyboardOpen ? metrics.inset : 0)}px`);
        root.classList.toggle("healthKeyboardOpen", keyboardOpen);

        if (keyboardOpen) {
          requestAnimationFrame(() => revealActiveField(behavior));
        }
      });
    };

    const scheduleSettledUpdate = () => {
      updateViewport("smooth");
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      settleTimers = [80, 220, 380].map((delay) => window.setTimeout(() => {
        if (!disposed) updateViewport("smooth");
      }, delay));
    };

    const clearKeyboardLayout = () => {
      nativeKeyboardHeight = 0;
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      settleTimers = [];
      root.classList.remove("healthKeyboardOpen");
      root.style.setProperty("--health-keyboard-inset", "0px");
    };

    const handleFocusOut = () => {
      window.setTimeout(() => {
        if (!disposed) updateViewport("smooth");
      }, 0);
    };

    const handleViewportScroll = () => updateViewport("smooth");

    const handleOrientationChange = () => {
      clearKeyboardLayout();
      window.setTimeout(() => {
        if (disposed) return;
        baselineHeight = Math.max(window.innerHeight || 0, viewport?.height || 0);
        updateViewport("auto");
      }, 220);
    };

    const installNativeListeners = async () => {
      if (!isIOSPlatform()) return;

      const showHandle = await Keyboard.addListener("keyboardWillShow", (info) => {
        nativeKeyboardHeight = Math.max(0, Math.round(Number(info?.keyboardHeight) || 0));
        scheduleSettledUpdate();
      });
      const hideHandle = await Keyboard.addListener("keyboardWillHide", clearKeyboardLayout);

      if (disposed) {
        showHandle.remove();
        hideHandle.remove();
        return;
      }
      nativeHandles = [showHandle, hideHandle];
    };

    viewport?.addEventListener("resize", scheduleSettledUpdate);
    viewport?.addEventListener("scroll", handleViewportScroll);
    window.addEventListener("resize", scheduleSettledUpdate);
    document.addEventListener("focusin", scheduleSettledUpdate);
    document.addEventListener("focusout", handleFocusOut);
    window.addEventListener("orientationchange", handleOrientationChange);
    installNativeListeners().catch(() => {});

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      settleTimers.forEach((timer) => window.clearTimeout(timer));
      nativeHandles.forEach((handle) => handle.remove());
      viewport?.removeEventListener("resize", scheduleSettledUpdate);
      viewport?.removeEventListener("scroll", handleViewportScroll);
      window.removeEventListener("resize", scheduleSettledUpdate);
      document.removeEventListener("focusin", scheduleSettledUpdate);
      document.removeEventListener("focusout", handleFocusOut);
      window.removeEventListener("orientationchange", handleOrientationChange);
      root.classList.remove("healthKeyboardOpen");
      root.style.removeProperty("--health-keyboard-inset");
    };
  }, [detailPageRef]);
}
